<template>
  <div v-if="hasAccess(MARKETING_MATERIALS_ACCESS, MARKETING_MATERIALS_MANAGE_ACCESS)"
       ref="socialMediaImages" class="marketing-materials social-media-images">
    <h1>Social Media Images</h1>
    <template v-if="pages && pages.length === 0">
      <notification-financing-page/>
    </template>
    <template v-else-if="pages">
      <PageSelector />

      <div class="columns">
        <div class="column social-media-images-title">
          <p v-html="description"></p>
        </div>
      </div>
      <b-field grouped group-multiline class="field-filters">
        <b-field
          v-if="themes.length > 1"
          label="Theme"
          custom-class="custom-label"
        >
          <ModernSelect
            v-model="filters.themeId"
            optionLabelKey="name"
            optionValueKey="id"
            :options="themes"
          />
        </b-field>

        <b-field class="field-radio-wrapper">
          <ModernRadioTabs
            v-model="filters.socialMediaId"
            :items="socialMedias"
            value-key="id"
            display-value-key="value"
            label="Social media"
          />
        </b-field>

        <b-field class="field-radio-wrapper">
          <ModernRadioTabs
            v-model="filters.contentTypeId"
            :items="socialMediaContentTypeOptions"
            value-key="id"
            display-value-key="value"
            label="Content type"
          />
        </b-field>

      </b-field>

      <b-field>
        <template v-if="images.length > 0">
          <ul
            v-if="imagesIsLoaded"
            class="images-list"
          >
            <li
              class="images-list-li"
              v-for="item in images"
              :key="item.id"
              @click="onDownloadClick(item)"
            >
              <img
                class="social-media-preview-image"
                :src="item.demoImageUrl"
                alt="Social media image example"
              >

              <div class="images-list-li-color-filter"></div>

              <div class="download-emulate-btn-wrapper">
                <div class="download-emulate-btn"></div>
              </div>
            </li>
          </ul>
        </template>
        <template v-else>
          <div class="columns is-centered">
            <div class="column is-half enh-disabled has-text-centered">
              <b-icon pack="fal" icon="frown" size="is-large"></b-icon>
              <p>Nothing here</p>
            </div>
          </div>
        </template>
      </b-field>
    </template>

    <a ref="downloadLink" style="display: none"></a>
  </div>
</template>

<script>
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import {
  download,
  search as getSocialMediaImageTemplates,
} from '@/api/social_media_image_templates';
import * as MUTATIONS from '@/store/mutations';
import NotificationFinancingPage from '@/components/NotificationFinancingPage.vue';
import {
  SOCIAL_MEDIA_CONTENT_TYPE_COVER_ID,
  SOCIAL_MEDIA_FACEBOOK_ID,
  SOCIAL_MEDIA_CONTENT_TYPES_MAP,
  CUSTOMER_TYPE_MEDICAL_ID,
} from '@/const/constants';
import ModernSelect from '@/components/ModernSelect.vue';
import { search as searchThemes } from '@/api/marketing_materials_theme';
import preloadImage from '@/utils/preloadImage';
import * as ACTIONS from '@/store/actions';
import { MARKETING_MATERIALS_ACCESS, MARKETING_MATERIALS_MANAGE_ACCESS } from '@/const/privileges';
import ModernRadioTabs from '@/components/Form/ModernRadioTabs.vue';
import PageSelector from '@/views/MarketingMaterials/components/PageSelector.vue';

export default {
  name: 'SocialMediaImages',
  components: {
    PageSelector,
    ModernRadioTabs,
    ModernSelect,
    NotificationFinancingPage,
  },
  data: () => ({
    MARKETING_MATERIALS_ACCESS,
    MARKETING_MATERIALS_MANAGE_ACCESS,
    filters: {
      socialMediaId: SOCIAL_MEDIA_FACEBOOK_ID,
      contentTypeId: SOCIAL_MEDIA_CONTENT_TYPE_COVER_ID,
      themeId: null,
    },
    imagesIsLoaded: false,
    images: [],
    themes: [],
    isActivated: false,
  }),
  computed: {
    ...mapGetters([
      'userAuthenticator',
      'hasAccess',
      'userTypeId',
      'pages',
      'selectedPage',
    ]),
    ...mapState('contractor', {
      contractorId: (state) => state.id,
      contractorTypeId: (state) => state.typeId,
    }),
    ...mapState('partner', { partnerId: (state) => state.id }),
    ...mapState('constant', ['socialMedias', 'socialMediaContentTypes']),

    windowWidth() {
      return this.$store.state.windowWidth;
    },
    socialMediaContentTypeOptions() {
      const allowedIds = this.filters.socialMediaId
        ? SOCIAL_MEDIA_CONTENT_TYPES_MAP[this.filters.socialMediaId]
        : [];

      return this.socialMediaContentTypes.filter((item) => allowedIds.includes(item.id));
    },
    description() {
      let clients = 'customers';

      if (this.partnerId) {
        clients = 'clients';
      } else if (this.contractorTypeId === CUSTOMER_TYPE_MEDICAL_ID) {
        clients = 'patients';
      }

      return 'Use your social networks to shout out about Enhancify advantages. Here you will find'
        + ' cover photos, images for posts and stories designed to fit Facebook, Instagram and'
        + ' LinkedIn.<br>'
        + ' To download an image, simply choose the Social Media, Content Type, and hit it. Easy'
        + ' like that!<br><br>'
        + ' Add a link to your financial portal in social networks to increase the number of your'
        + ` ${clients}! For this:<br>`
        + ' &nbsp;&nbsp;&nbsp;&nbsp;• when using images for posts, add a link to your portal'
        + ' to the text of the post<br>'
        + ' &nbsp;&nbsp;&nbsp;&nbsp;• when using images for stories, add a link to your portal'
        + ' to the story itself';
    },
  },
  deactivated() {
    this.isActivated = false;
    this.images = [];
  },
  async activated() {
    if (!this.contractorId && !this.partnerId) {
      this.isActivated = true;
      return;
    }

    this.startLoading();

    try {
      if (!this.selectedPage?.id) {
        await this.loadPages(this.contractorId ? this.contractorId : null);
      }

      if (this.selectedPage?.id) {
        const themesResponse = await searchThemes({ userTypeId: this.userTypeId });
        this.themes = themesResponse.data;
        this.filters.themeId = this.themes[0].id;

        await this.searchImages();
      }

      window.scrollTo(0, 0);
    } catch (error) {
      this.$buefy.snackbar.open({
        message: 'An unexpected error occurred, please contact support@enhancify.com',
        actionText: ' ',
        position: 'is-top',
        indefinite: true,
        type: 'is-danger',
      });
    }

    this.finishLoading();
    this.isActivated = true;
  },
  watch: {
    'filters.themeId': 'searchImagesByWatcher',
    'filters.socialMediaId': function filtersSocialMediaId() {
      const contentTypeExists = this.socialMediaContentTypeOptions
        .find((item) => item.id === this.filters.contentTypeId);

      if (contentTypeExists === undefined) {
        this.filters.contentTypeId = this.socialMediaContentTypeOptions[0].id;
      } else {
        this.searchImagesByWatcher();
      }
    },
    'filters.contentTypeId': 'searchImagesByWatcher',
    async selectedPage() {
      await this.changePage();
    },
  },
  methods: {
    ...mapActions({
      loadPages: ACTIONS.LOAD_PAGES,
      setSelectedPage: ACTIONS.SET_SELECTED_PAGE,
    }),
    ...mapMutations({
      startLoading: MUTATIONS.START_LOADING,
      finishLoading: MUTATIONS.FINISH_LOADING,
    }),

    async changePage() {
      this.filters.socialMediaId = SOCIAL_MEDIA_FACEBOOK_ID;
      if (this.isActivated) {
        await this.searchImages();
      }
    },
    async searchImagesByWatcher() {
      if (this.isActivated) {
        await this.searchImages();
      }
    },
    async searchImages() {
      this.images = [];
      if (!this.selectedPage?.id) {
        return;
      }

      this.startLoading();
      this.imagesIsLoaded = false;

      const params = {
        socialMediaId: this.filters.socialMediaId,
        contentTypeId: this.filters.contentTypeId,
        userTypeId: this.userTypeId,
        themeId: this.filters.themeId,
        order: 'order',
      };
      const { data } = await getSocialMediaImageTemplates(params);
      this.images = [];

      const promises = [];
      data.data.forEach((item) => {
        this.images.push(item);
        const imageUrl = item.demoImageUrl;
        promises.push(preloadImage(imageUrl));
      });

      try {
        await Promise.all(promises);
      } catch (error) {
        // pass
      }

      this.imagesIsLoaded = true;
      this.finishLoading();
    },
    async onDownloadClick(item) {
      this.startLoading();

      try {
        const { data: blob } = await download(
          Number(item.id),
          this.$store.state.user.id,
          this.selectedPage.id,
        );
        const file = window.URL.createObjectURL(new Blob([blob], {
          type: 'image/png',
        }));

        const el = this.$refs.downloadLink;
        const filename = `${item.socialMedia.value}_${item.contentType.value}.png`.toLowerCase();
        el.setAttribute('href', file);
        el.setAttribute('rel', 'noopener noreferrer');
        el.setAttribute('download', filename);
        el.click();
      } catch (error) {
        this.$buefy.snackbar.open({
          message: 'Something wrong',
          actionText: ' ',
          position: 'is-top',
          indefinite: true,
          type: 'is-danger',
        });
      }

      this.finishLoading();
    },
  },
};
</script>

<style lang="stylus">
@require "../../assets/style/variables.styl";

.marketing-materials.social-media-images
  & .field-radio-wrapper
    @media(max-width:499px)
      width 100%
  & .custom-label
    font-size 16px;
    font-weight 400;
    line-height 24px;
    color #969ea2
  & .field-filters
    margin-top 18px
    margin-bottom 32px !important
</style>

<style lang="stylus" scoped>
.social-media-images-title
  max-width 860px
@media(max-width:365px)
  .content-types >>> ul
  .social-media-types >>> ul
    width 100%
    flex-wrap wrap
    li:first-child a
      border-top-left-radius 4px
      border-bottom-left-radius 0
    li:nth-child(2) a
      border-top-right-radius 4px
    li:last-child a
      border-top-right-radius 0
      border-bottom-left-radius 4px
  .social-media-types >>> ul li:nth-child(2),
  .social-media-types >>> ul li:nth-child(3)
    margin-left 0
  .social-media-types >>> ul li:not(.is-active):nth-child(1) a
    border-bottom none
  .social-media-types >>> ul li:not(.is-active):nth-child(2) a
    border-left none
    border-bottom none

.social-media-field.block-title
  margin-bottom 0.5rem

.images-list-li-color-filter
  position: absolute;
  top: 0;
  left: 0;
  width 100%
  height 100%
  background-color: rgba(41,48,51,0.2);

.images-list
  display flex
  align-items flex-start
  flex-wrap wrap
  gap 20px
.images-list li
  position relative
  display flex
  justify-content center
  align-items center
  min-height 80px
  min-width 80px
  img
    width: auto;
    height: auto;
    max-width: none;
.download-emulate-btn-wrapper
  display flex
  align-items center
  justify-content center
  width 100%
  height 100%
  cursor pointer
  position absolute
  top 0
  left 0
.download-emulate-btn-wrapper div
  width 74px
  height 74px
  border-radius 37px
  background-color #FFFFFF
  position relative

.download-emulate-btn-wrapper div:before
  content url("data:image/svg+xml,%3Csvg width='50' height='33' viewBox='0 0 50 33' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M33.3054 13.2732V17.8831H16.6937V13.2732H15.2059V18.626C15.2059 19.0358 15.54 19.3699 15.9498 19.3699H34.0493C34.4571 19.3699 34.7912 19.0367 34.7941 18.626V13.2732H33.3063H33.3054Z' fill='%23525F66'/%3E%3Cpath d='M29.5178 10.3331L28.4647 9.28191L25.7428 12.0038V0H24.256V12.0047L21.5322 9.28191L20.4819 10.3341L24.9989 14.8511L29.5178 10.3331Z' fill='%23525F66'/%3E%3Cpath d='M1.66661 25.734C2.71886 25.734 3.5587 25.9703 4.18613 26.4427C4.8201 26.9152 5.13708 27.7858 5.13708 29.0544C5.13708 30.2971 4.82663 31.1741 4.20574 31.6855C3.58485 32.1903 2.79729 32.4427 1.84308 32.4427H0V25.734H1.66661ZM1.29407 26.6952V31.4816H1.93131C2.50645 31.4816 2.95742 31.3068 3.2842 30.9573C3.61099 30.6013 3.77438 29.967 3.77438 29.0544C3.77438 28.4201 3.68942 27.9314 3.51949 27.5884C3.3561 27.2389 3.13715 27.0026 2.86265 26.8796C2.59468 26.7567 2.27117 26.6952 1.89209 26.6952H1.29407Z' fill='%23525F66'/%3E%3Cpath d='M8.95497 25.5884C9.56279 25.5884 10.0889 25.7243 10.5333 25.9961C10.9778 26.268 11.3176 26.666 11.5529 27.1903C11.7947 27.7146 11.9157 28.3457 11.9157 29.0835C11.9157 29.8149 11.7947 30.4427 11.5529 30.967C11.3111 31.4913 10.968 31.8926 10.5235 32.1709C10.0791 32.4492 9.55625 32.5884 8.95497 32.5884C8.34714 32.5884 7.82102 32.4525 7.37659 32.1806C6.93216 31.9088 6.58903 31.5107 6.34721 30.9864C6.10539 30.4622 5.98448 29.8311 5.98448 29.0932C5.98448 28.3683 6.10539 27.7437 6.34721 27.2194C6.58903 26.6887 6.93216 26.2842 7.37659 26.0059C7.82755 25.7275 8.35368 25.5884 8.95497 25.5884ZM8.95497 26.569C8.43211 26.569 8.03343 26.7728 7.75893 27.1806C7.48443 27.5819 7.34718 28.2194 7.34718 29.0932C7.34718 30.7696 7.88311 31.6078 8.95497 31.6078C9.47783 31.6078 9.87324 31.4071 10.1412 31.0059C10.4157 30.6046 10.553 29.9638 10.553 29.0835C10.553 28.2097 10.4157 27.5722 10.1412 27.1709C9.87324 26.7696 9.47783 26.569 8.95497 26.569Z' fill='%23525F66'/%3E%3Cpath d='M20.3899 25.734L19.1449 32.4427H17.4587L16.4293 27.0932L15.3705 32.4427H13.7333L12.5078 25.734H13.7921L14.6548 31.4233L15.7626 25.734H17.1351L18.2233 31.4233L19.1645 25.734H20.3899Z' fill='%23525F66'/%3E%3Cpath d='M26.3399 32.4427H24.6635L22.34 27.0156C22.3988 27.4945 22.4413 27.9282 22.4675 28.3165C22.5001 28.7049 22.5165 29.1677 22.5165 29.7049V32.4427H21.34V25.734H22.9772L25.3399 31.1709C25.3007 30.912 25.2615 30.5722 25.2223 30.1515C25.183 29.7243 25.1634 29.3004 25.1634 28.8796V25.734H26.3399V32.4427Z' fill='%23525F66'/%3E%3Cpath d='M29.2209 25.734V31.3942H31.9365L31.7894 32.4427H27.9268V25.734H29.2209Z' fill='%23525F66'/%3E%3Cpath d='M35.0628 25.5884C35.6706 25.5884 36.1967 25.7243 36.6412 25.9961C37.0856 26.268 37.4254 26.666 37.6607 27.1903C37.9025 27.7146 38.0235 28.3457 38.0235 29.0835C38.0235 29.8149 37.9025 30.4427 37.6607 30.967C37.4189 31.4913 37.0758 31.8926 36.6313 32.1709C36.1869 32.4492 35.6641 32.5884 35.0628 32.5884C34.455 32.5884 33.9288 32.4525 33.4844 32.1806C33.04 31.9088 32.6968 31.5107 32.455 30.9864C32.2132 30.4622 32.0923 29.8311 32.0923 29.0932C32.0923 28.3683 32.2132 27.7437 32.455 27.2194C32.6968 26.6887 33.04 26.2842 33.4844 26.0059C33.9354 25.7275 34.4615 25.5884 35.0628 25.5884ZM35.0628 26.569C34.5399 26.569 34.1412 26.7728 33.8667 27.1806C33.5922 27.5819 33.455 28.2194 33.455 29.0932C33.455 30.7696 33.9909 31.6078 35.0628 31.6078C35.5856 31.6078 35.981 31.4071 36.249 31.0059C36.5235 30.6046 36.6608 29.9638 36.6608 29.0835C36.6608 28.2097 36.5235 27.5722 36.249 27.1709C35.981 26.7696 35.5856 26.569 35.0628 26.569Z' fill='%23525F66'/%3E%3Cpath d='M42.7355 32.4427L42.3139 30.8893H40.0689L39.6473 32.4427H38.3042L40.412 25.734H42.01L44.108 32.4427H42.7355ZM40.3238 29.9185H42.0492L41.1865 26.7146L40.3238 29.9185Z' fill='%23525F66'/%3E%3Cpath d='M46.5295 25.734C47.5818 25.734 48.4216 25.9703 49.0491 26.4427C49.683 26.9152 50 27.7858 50 29.0544C50 30.2971 49.6895 31.1741 49.0687 31.6855C48.4478 32.1903 47.6602 32.4427 46.706 32.4427H44.8629V25.734H46.5295ZM46.157 26.6952V31.4816H46.7942C47.3694 31.4816 47.8203 31.3068 48.1471 30.9573C48.4739 30.6013 48.6373 29.967 48.6373 29.0544C48.6373 28.4201 48.5523 27.9314 48.3824 27.5884C48.219 27.2389 48.0001 27.0026 47.7256 26.8796C47.4576 26.7567 47.1341 26.6952 46.755 26.6952H46.157Z' fill='%23525F66'/%3E%3C/svg%3E")
  display block
  top 15px
  left 12px
  position absolute
</style>
